import React, { useEffect, useState } from 'react';

import { Chip, IconButton, Stack, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { MenuDay } from '@core/interfaces/api';

import {
  calculateInitialDate,
  calculateNextOccurrence,
  formatDate,
} from '../utils/date.utils';
import { MenuRowExpandedContent } from './menu-row-expanded-content';

interface MenuSlidesViewProps {
  days: MenuDay[];
  cycleStartDate: string;
  daysInMenu: number;
}

function MenuSlidesView({
  days,
  cycleStartDate,
  daysInMenu,
}: MenuSlidesViewProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [days.length]);

  if (!days.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No menu days available.
      </Typography>
    );
  }

  const currentDay = days[index];
  const handlePrev = () => setIndex((prev) => Math.max(prev - 1, 0));
  const handleNext = () =>
    setIndex((prev) => Math.min(prev + 1, days.length - 1));

  return (
    <div className="flex flex-col w-full h-full">
      <div className="bg-white w-full flex flex-col items-center py-6">
        <span className="leading-tight text-[#6ba4b8] text-5xl font-bold uppercase">
          Today's Dining Menu
        </span>
        <p className="text-lg text-gray-800 text-center tracking-tight">
          Subject to change. See posted menu in dining room for most current
          selections.
        </p>
      </div>

      <div className="bg-[#e2ebef] flex flex-col items-center w-full justify-start py-6 px-6 gap-4">
        <MenuRowExpandedContent data={currentDay.menuData} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2, width: '100%' }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={index === 0}
            aria-label="Previous day"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${currentDay.dayOfWeek || `Day ${currentDay.day}`}`}
              size="small"
            />
            <Chip
              label={`Day ${currentDay.day}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Week ${currentDay.menuWeek}`}
              size="small"
              variant="outlined"
            />
          </Stack>
          <IconButton
            onClick={handleNext}
            disabled={index === days.length - 1}
            aria-label="Next day"
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          justifyContent="center"
          sx={{ mt: 2, width: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            First Occurrence:{' '}
            {formatDate(calculateInitialDate(cycleStartDate, currentDay.day))}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Next Occurrence:{' '}
            {formatDate(
              calculateNextOccurrence(
                cycleStartDate,
                currentDay.day,
                daysInMenu,
              ),
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`Day ${index + 1} of ${days.length}`}
          </Typography>
        </Stack>
      </div>
    </div>
  );
}

export { MenuSlidesView, type MenuSlidesViewProps };
